import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getVendorMenus,
  createMenu,
  updateMenu,
  deleteMenu,
  restoreMenu,
  permanentlyDeleteMenu,
  assignMenuToSections,
  getVendorSections,
  getSectionMenus,
  getMenuSections,
  // Add kitchen APIs
  createKitchen,
  getPrinters
} from '../api/api';
import { useTheme } from '../context/ThemeContext';
import { useConfirm } from '../context/ConfirmContext';
import {
  Container,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem as MuiMenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormGroup,
  Tabs,
  Tab,
  Fade,
  useMediaQuery,
  useTheme as useMuiTheme,
  Divider,
  Paper,
  Snackbar,
} from '@mui/material';
import { PageHeroBand, SecondaryButton } from '../components/ui';
import LoadingScreen from '../components/LoadingScreen';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import {
  AddOutlined as AddIcon,
  EditOutlined as EditIcon,
  DeleteOutlineOutlined as DeleteIcon,
  RestoreOutlined as RestoreIcon,
  MenuBookOutlined as MenuBookIcon,
  Schedule as TimeIcon,
  EventNote as CalendarIcon,
  DiningOutlined as RestaurantMenuIcon,
  TuneOutlined as SettingsIcon,
  FreeBreakfastOutlined as BreakfastIcon,
  LunchDiningOutlined as LunchIcon,
  DinnerDiningOutlined as DinnerIcon,
  LocalCafeOutlined as DrinkIcon,
  Star as StarIcon,
  RoomOutlined as LocationIcon,
  // Add kitchen icons
  KitchenOutlined as KitchenIcon,
  LocalBarOutlined as BarIcon,
  RestaurantOutlined as RestaurantIcon,
  CoffeeOutlined as CafeIcon,
  CakeOutlined as CakeIcon,
  ScienceOutlined as ScienceIcon,
  Inventory2Outlined as ItemsIcon,
  ShoppingBagOutlined as TakeawayIcon,
} from '@mui/icons-material';

interface Menu {
  id: number;
  name: string;
  description?: string;
  menuType: string;
  isActive: boolean;
  isDefault: boolean;
  availableFrom?: string;
  availableTo?: string;
  availableDays?: string[] | string; // Can be array or comma-separated string from backend
  itemCount?: number;
  sectionCount?: number;
  assignedSections?: Array<{id: number; name: string; floor: string}>;
  isDeleted?: boolean;
  deletedAt?: string;
}

interface Section {
  id: number;
  name: string;
  floorName?: string;
  isSpecial?: boolean;
}

// Special section ID for Takeaway menu assignment
const TAKEAWAY_SECTION_ID = -1;

const MENU_TYPES = [
  { value: 'REGULAR', label: 'Regular Menu', icon: <RestaurantMenuIcon /> },
  { value: 'BREAKFAST', label: 'Breakfast', icon: <BreakfastIcon /> },
  { value: 'LUNCH', label: 'Lunch', icon: <LunchIcon /> },
  { value: 'DINNER', label: 'Dinner', icon: <DinnerIcon /> },
  { value: 'DRINKS', label: 'Drinks', icon: <DrinkIcon /> },
  { value: 'SPECIAL', label: 'Special', icon: <StarIcon /> }
];

const WEEKDAYS = [
  { value: 'MON', label: 'Monday' },
  { value: 'TUE', label: 'Tuesday' },
  { value: 'WED', label: 'Wednesday' },
  { value: 'THU', label: 'Thursday' },
  { value: 'FRI', label: 'Friday' },
  { value: 'SAT', label: 'Saturday' },
  { value: 'SUN', label: 'Sunday' }
];

// Optional kitchen type suggestions (vendor can ignore and type their own)
const KITCHEN_SUGGESTIONS = [
  'Main Kitchen',
  'Bar Area',
  'Dining Area', 
  'Pool Area',
  'Rooftop Kitchen',
  'VIP Kitchen',
  'Dessert Station',
  'Cold Kitchen',
  'Grill Station'
];

interface Kitchen {
  id: number;
  name: string;
  type: string;
  description?: string;
  isActive: boolean;
  printerId?: number;
}

interface Printer {
  id: number;
  name: string;
  ipAddress: string;
  isActive: boolean;
}

const MultiMenuManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { theme, currentThemeName } = useTheme();
  const muiTheme = useMuiTheme();
  const confirm = useConfirm();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [menus, setMenus] = useState<Menu[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState<Menu | null>(null);
  const [selectedMenu, setSelectedMenu] = useState<Menu | null>(null);
  const [selectedSections, setSelectedSections] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  // Helper function to normalize availableDays to array format
  const normalizeAvailableDays = (days?: string[] | string): string[] => {
    if (!days) return [];
    if (Array.isArray(days)) return days;
    if (typeof days === 'string') {
      return days.split(',').map(d => d.trim()).filter(d => d.length > 0);
    }
    return [];
  };
  
  // Map: sectionId -> assigned non-default menu (for disabling in Default view)
  const sectionIdToAssignedMenu = useMemo(() => {
    const map = new Map<number, { id: number; name: string }>();
    (menus || []).forEach((menu) => {
      if (menu.isDefault) return; // ignore default
      (menu.assignedSections || []).forEach((s) => {
        map.set(Number(s.id), { id: Number(menu.id), name: String(menu.name) });
      });
    });
    return map;
  }, [menus]);
  
  // Dedupe sections by logical key: name + floorName (case-insensitive)
  const { dedupedSections, idToCanonicalId } = useMemo(() => {
    const keyToCanonical: Record<string, Section> = {};
    const idToCanon: Record<number, number> = {};
    (sections || []).forEach((s) => {
      const key = `${String(s.name || '').trim().toLowerCase()}|${String(s.floorName || '').trim().toLowerCase()}`;
      if (!keyToCanonical[key]) {
        keyToCanonical[key] = s; // first occurrence becomes canonical
      }
      idToCanon[s.id] = keyToCanonical[key].id;
    });
    return {
      dedupedSections: Object.values(keyToCanonical),
      idToCanonicalId: idToCanon,
    };
  }, [sections]);

  // Kitchen management state
  const [kitchenDialogOpen, setKitchenDialogOpen] = useState(false);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [kitchenFormData, setKitchenFormData] = useState({
    name: '',
    type: '',  // Now free text input
    description: '',
    isActive: true,
    printerId: ''
  });
  const [formData, setFormData] = useState<Partial<Menu>>({
    name: '',
    description: '',
    menuType: 'REGULAR',
    isActive: true,
    isDefault: false,
    availableDays: []
  });
  const [selectNewDefaultDialogOpen, setSelectNewDefaultDialogOpen] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<any>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [menus, sections, printers] = await Promise.all([
        getVendorMenus(true), // Include soft-deleted menus for menu management page
        getVendorSections(),
        getPrinters()
      ]);
      // Build assignedSections per menu by querying assignments per section
      setSections(sections);
      setPrinters(printers || []);

      try {
        const sectionAssignmentResults = await Promise.all(
          (sections || []).map((section: any) =>
            getSectionMenus(section.id).then((res: any) => ({
              section: res?.section,
              assignments: res?.assignments || []
            }))
          )
        );

        const menuIdToAssignedSections: Record<number, Array<{ id: number; name: string; floor: string }>> = {};
        const menuIdToSectionIdSet: Record<number, Set<number>> = {};

        sectionAssignmentResults.forEach((result: any) => {
          const section = result.section;
          if (!section) return;
          const { id: sectionId, name: sectionName, floor } = section;
          (result.assignments || []).forEach((assignment: any) => {
            const menuId = Number(assignment.menuId);
            if (!menuIdToAssignedSections[menuId]) {
              menuIdToAssignedSections[menuId] = [];
              menuIdToSectionIdSet[menuId] = new Set<number>();
            }
            if (!menuIdToSectionIdSet[menuId].has(Number(sectionId))) {
              menuIdToAssignedSections[menuId].push({
                id: Number(sectionId),
                name: String(sectionName),
                floor: String(floor)
              });
              menuIdToSectionIdSet[menuId].add(Number(sectionId));
            }
          });
        });

        const enrichedMenus = (menus || []).map((menu: any) => ({
          ...menu,
          assignedSections: menuIdToAssignedSections[Number(menu.id)] || []
        }));

        setMenus(enrichedMenus);
      } catch (enrichErr) {
        // Fallback to original menus if enrichment fails
        setMenus(menus);
      }
    } catch (err) {
      setError('Failed to fetch data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (menu?: Menu) => {
    if (menu) {
      setEditingMenu(menu);
      // Normalize availableDays to array format for form editing
      setFormData({
        ...menu,
        availableDays: normalizeAvailableDays(menu.availableDays)
      });
    } else {
      setEditingMenu(null);
      setFormData({
        name: '',
        description: '',
        menuType: 'REGULAR',
        isActive: true,
        isDefault: false,
        availableDays: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMenu(null);
  };

  const handleSave = async () => {
    try {
      setError(null);

      // Validation: Check if trying to remove default menu without selecting another default
      if (editingMenu && editingMenu.isDefault && !formData.isDefault) {
        // User is trying to turn off default on a currently default menu
        const otherActiveMenus = menus.filter(m =>
          m.id !== editingMenu.id && m.isActive && !m.isDeleted
        );

        if (otherActiveMenus.length === 0) {
          setError('Cannot remove default menu. You must have at least one active menu. Please create another menu first.');
          return;
        }

        // Show dialog to select new default menu
        setSelectNewDefaultDialogOpen(true);

        // Store the save data to process after user selects new default
        const availableDaysString = Array.isArray(formData.availableDays)
          ? formData.availableDays.join(',')
          : formData.availableDays || '';

        setPendingSaveData({
          name: formData.name || '',
          description: formData.description,
          menuType: formData.menuType || 'REGULAR',
          isActive: formData.isActive,
          isDefault: formData.isDefault,
          availableFrom: formData.availableFrom,
          availableTo: formData.availableTo,
          availableDays: availableDaysString
        });
        return;
      }

      // Normal save flow
      const availableDaysString = Array.isArray(formData.availableDays)
        ? formData.availableDays.join(',')
        : formData.availableDays || '';

      const menuData = {
        name: formData.name || '',
        description: formData.description,
        menuType: formData.menuType || 'REGULAR',
        isActive: formData.isActive,
        isDefault: formData.isDefault,
        availableFrom: formData.availableFrom,
        availableTo: formData.availableTo,
        availableDays: availableDaysString
      };

      if (editingMenu) {
        await updateMenu(editingMenu.id, menuData);
        setSuccess('Menu updated successfully');
      } else {
        await createMenu(menuData);
        setSuccess('Menu created successfully');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      setError((err as any).response?.data?.error || 'Failed to save menu');
    }
  };

  const handleSelectNewDefault = async (newDefaultMenuId: number) => {
    try {
      setError(null);

      // First, set the selected menu as the new default
      await updateMenu(newDefaultMenuId, { isDefault: true });

      // Then, save the current menu with isDefault: false
      if (editingMenu && pendingSaveData) {
        await updateMenu(editingMenu.id, pendingSaveData);
      }

      setSuccess('Default menu updated successfully');
      setSelectNewDefaultDialogOpen(false);
      setPendingSaveData(null);
      handleCloseDialog();
      fetchData();
    } catch (err) {
      setError((err as any).response?.data?.error || 'Failed to update default menu');
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({
      title: 'Delete menu',
      description: 'Are you sure you want to delete this menu?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    }))) return;

    try {
      await deleteMenu(id);
      setSuccess('Menu deleted successfully. You can restore it from the trash.');
      fetchData();
    } catch (err) {
      setError((err as any).response?.data?.error || 'Failed to delete menu');
    }
  };

  const handleRestore = async (id: number) => {
    try {
      await restoreMenu(id);
      setSuccess('Menu restored successfully');
      fetchData();
    } catch (err) {
      setError((err as any).response?.data?.error || 'Failed to restore menu');
    }
  };

  const handlePermanentDelete = async (id: number, menuName: string) => {
    if (!(await confirm({
      title: 'Permanently delete menu',
      description: `Are you absolutely sure you want to PERMANENTLY delete "${menuName}"? This action CANNOT be undone. All menu items, categories, and assignments will be lost forever.`,
      confirmText: 'Permanently delete',
      cancelText: 'Cancel',
    }))) {
      return;
    }

    // Second confirmation
    if (!(await confirm({
      title: 'Final confirmation',
      description: `This is your last chance to cancel. Permanently delete "${menuName}"?`,
      confirmText: 'Permanently delete',
      cancelText: 'Cancel',
    }))) {
      return;
    }

    try {
      await permanentlyDeleteMenu(id);
      setSuccess('Menu permanently deleted');
      fetchData();
    } catch (err) {
      setError((err as any).response?.data?.error || 'Failed to permanently delete menu');
    }
  };

  const handleOpenAssignDialog = async (menu: Menu) => {
    setSelectedMenu(menu);
    
    // Always fetch fresh data from API for accurate pre-selection
    try {
      const assignedSectionIds = await getMenuSections(menu.id);
      console.log('Fetched section IDs from API for menu', menu.name, ':', assignedSectionIds);
      
      // Ensure IDs are numbers for comparison
      const numericIds = Array.isArray(assignedSectionIds) 
        ? assignedSectionIds.map(id => {
            const numId = typeof id === 'string' ? parseInt(id, 10) : id;
            return !isNaN(numId) ? numId : null;
          }).filter(id => id !== null)
        : [];
      
      console.log('Processed section IDs:', numericIds);
      console.log('Available sections:', dedupedSections.map(s => ({ id: s.id, name: s.name })));
      console.log('ID mapping:', idToCanonicalId);
      
      // Map the IDs through the canonical mapping if needed
      const mappedIds = numericIds.map(id => {
        // Check if this ID needs to be mapped to a canonical ID
        const canonicalId = Object.entries(idToCanonicalId).find(([key, val]) => val === id)?.[0];
        return canonicalId ? parseInt(canonicalId, 10) : id;
      });
      
      console.log('Final selected section IDs:', mappedIds);
      setSelectedSections(mappedIds);
    } catch (err) {
      console.error('Failed to fetch assigned sections from API:', err);
      // Fall back to menu object if API fails
      if (menu.assignedSections && menu.assignedSections.length > 0) {
        const assignedIds = menu.assignedSections.map(section => {
          const canonicalId = idToCanonicalId[section.id];
          return canonicalId !== undefined ? canonicalId : section.id;
        });
        console.log('Using fallback assigned sections for menu', menu.name, ':', assignedIds);
        setSelectedSections(Array.from(new Set(assignedIds)));
      } else {
        setSelectedSections([]);
      }
    }
    
    setAssignDialogOpen(true);
  };

  const handleAssignSections = async () => {
    if (!selectedMenu) return;
    
    try {
      await assignMenuToSections(selectedMenu.id, selectedSections);
      setSuccess('Sections assigned successfully');
      setAssignDialogOpen(false);
      fetchData();
    } catch (err) {
      setError((err as any).response?.data?.error || 'Failed to assign sections');
    }
  };

  const handleManageItems = (menuId: number, menuName: string) => {
    // Open in a modal or slide-out panel instead of navigating
    // For now, we'll still navigate but with better UX
    navigate(`/admin/vendors/${user?.id}/menu`, { 
      state: { menuId, menuName } 
    });
  };

  const getMenuIcon = (type: string) => {
    const menuType = MENU_TYPES.find(t => t.value === type);
    return menuType?.icon || <RestaurantMenuIcon />;
  };

  const getMenuTypeColor = (type: string) => {
    switch (type) {
      case 'BREAKFAST': return 'warning';
      case 'LUNCH': return 'info';
      case 'DINNER': return 'primary';
      case 'DRINKS': return 'secondary';
      case 'SPECIAL': return 'error';
      default: return 'default';
    }
  };

  // Kitchen creation functions
  const handleOpenKitchenDialog = (suggestedName?: string) => {
    setKitchenFormData({
      name: suggestedName || '',
      type: '',
      description: '',
      isActive: true,
      printerId: ''
    });
    setKitchenDialogOpen(true);
  };

  const handleCloseKitchenDialog = () => {
    setKitchenDialogOpen(false);
    setKitchenFormData({
      name: '',
      type: '',
      description: '',
      isActive: true,
      printerId: ''
    });
  };

  const handleCreateKitchen = async () => {
    try {
      const kitchenData = {
        name: kitchenFormData.name.trim(),
        type: kitchenFormData.type.trim() || 'GENERAL', // Use GENERAL as default if empty
        description: kitchenFormData.description.trim(),
        isActive: kitchenFormData.isActive,
        printerId: kitchenFormData.printerId ? parseInt(kitchenFormData.printerId) : undefined
      };
      
      console.log('Creating kitchen with data:', kitchenData); // Debug log
      
      await createKitchen(kitchenData);
      setSuccess(`Kitchen "${kitchenData.name}" created successfully!`);
      handleCloseKitchenDialog();
      // Optionally refresh data if needed
    } catch (err: any) {
      console.error('Kitchen creation error:', err); // Debug log
      setError(err.response?.data?.error || err.message || 'Failed to create kitchen');
    }
  };

  const getSuggestedKitchenName = (menuType: string) => {
    switch (menuType) {
      case 'DRINKS': return 'Bar Area';
      case 'BREAKFAST': return 'Cafe Area';
      case 'DESSERTS': return 'Dessert Station';
      case 'LUNCH': return 'Main Dining';
      case 'DINNER': return 'Main Dining';
      default: return 'Main Kitchen';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header — confident-shell hero band, same as the Finance module */}
          <PageHeroBand
            eyebrow="Settings · Menus"
            title="Menu Management"
            heroLabel="Active menus"
            heroValue={String(menus.filter(m => m.isActive).length)}
            actions={<Box sx={{ display: 'flex', gap: 1, flexShrink: 0,
              '& .MuiButton-root': { backgroundColor: 'color-mix(in srgb, var(--band-text) 10%, transparent)', color: 'var(--band-text)', borderColor: 'color-mix(in srgb, var(--band-text) 25%, transparent)', '&:hover': { backgroundColor: 'color-mix(in srgb, var(--band-text) 18%, transparent)', borderColor: 'color-mix(in srgb, var(--band-text) 40%, transparent)' } } }}>
              <SecondaryButton
                onClick={() => handleOpenDialog()}
                startIcon={<AddIcon className="w-5 h-5" />}
                data-testid="create-menu-button"
                sx={{ fontSize: '0.813rem', px: 1.5 }}
              >
                Create New Menu
              </SecondaryButton>
            </Box>}
          />

          {/* Summary cards — white surface, honest zero state (see FinanceDashboard) */}
          <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
            {[
              { label: 'Total Menus', value: menus.length },
              { label: 'Default Menu', value: menus.filter(m => m.isDefault).length },
            ].map((card) => (
              <Grid item xs={6} sm={6} md={6} key={card.label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: '12px',
                    backgroundColor: 'var(--bg-surface)',
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--text-tertiary)',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {card.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: card.value === 0 ? 600 : 700,
                      color: card.value === 0 ? 'var(--text-tertiary)' : 'var(--text-primary)',
                      fontSize: { xs: '1.35rem', sm: '1.75rem' },
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.15,
                      mt: 0.25,
                    }}
                  >
                    {card.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Alerts */}
          {error && (
            <Fade in>
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            </Fade>
          )}
          {success && (
            <Fade in>
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            </Fade>
          )}

          {/* Loading State */}
          {loading ? (
            <Box py={6}>
              <LoadingScreen message="Loading menus..." />
            </Box>
          ) : (
            /* Menu Cards */
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 items-start">
              {menus.map((menu) => (
                <div key={menu.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-200 overflow-hidden h-full flex flex-col"
                     style={{
                       opacity: menu.isDeleted ? 0.6 : 1,
                       filter: menu.isDeleted ? 'grayscale(50%)' : 'none'
                     }}>
                  <div className="p-4 sm:p-6 flex-1 flex flex-col">
                    {/* Header */}
                    <div className="mb-5">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-12 h-12 flex-shrink-0 rounded-xl flex items-center justify-center"
                             style={{ 
                               backgroundColor: theme.components?.card?.hover || theme.hover,
                             }}>
                          {getMenuIcon(menu.menuType)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 leading-tight mb-1">
                            {menu.name}
                          </h3>
                          {menu.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
                              {menu.description}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Status Badges */}
                      <div className="flex flex-wrap gap-2">
                        {menu.isDeleted && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200 text-xs font-medium rounded-full">
                            <DeleteIcon className="w-3 h-3 flex-shrink-0" />
                            Deleted
                          </span>
                        )}
                        {menu.isDefault && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200 text-xs font-medium rounded-full">
                            <StarIcon className="w-3 h-3 flex-shrink-0" />
                            Default
                          </span>
                        )}
                        <span className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full ${
                          menu.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {menu.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>

                    {/* Menu Info */}
                    <div className="space-y-3">
                      {/* Menu Type */}
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full`}
                              style={{
                                backgroundColor:
                                  menu.menuType === 'BREAKFAST' ? (theme.button?.warning?.bg || '#FEF3C7') :
                                  menu.menuType === 'LUNCH' ? theme.button?.secondary?.bg :
                                  menu.menuType === 'DINNER' ? (theme.button?.secondary?.bg || '#F3F4F6') :
                                  menu.menuType === 'DRINKS' ? (theme.button?.secondary?.bg || '#FEF3C7') :
                                  menu.menuType === 'SPECIAL' ? (theme.button?.danger?.bg || '#FEE2E2') :
                                  theme.gray[100],
                                color:
                                  menu.menuType === 'BREAKFAST' ? theme.warning :
                                  menu.menuType === 'LUNCH' ? theme.primary :
                                  menu.menuType === 'DINNER' ? theme.secondary :
                                  menu.menuType === 'DRINKS' ? theme.accent :
                                  menu.menuType === 'SPECIAL' ? theme.danger :
                                  theme.text
                              }}>
                          {getMenuIcon(menu.menuType)}
                          {menu.menuType}
                        </span>
                      </div>

                      {/* Time Availability */}
                      {(menu.availableFrom || menu.availableTo) && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <TimeIcon className="w-4 h-4" />
                          <span>{menu.availableFrom || '00:00'} - {menu.availableTo || '23:59'}</span>
                        </div>
                      )}

                      {/* Day Availability */}
                      {(() => {
                        const days = normalizeAvailableDays(menu.availableDays);
                        return days.length > 0 && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <CalendarIcon className="w-4 h-4" />
                            <div className="flex flex-wrap gap-1">
                              {days.map(day => (
                                <span key={day} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                                  {day}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Stats */}
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <div className="relative">
                            <ItemsIcon className="w-5 h-5 text-gray-400" />
                            <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 flex items-center justify-center text-white text-xs font-medium rounded-full px-1"
                                  style={{ backgroundColor: theme.primary }}>
                              {menu.itemCount || 0}
                            </span>
                          </div>
                          <span className="text-gray-600 dark:text-gray-400">Items</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                      {menu.isDeleted ? (
                        /* Actions for Deleted Menus */
                        <>
                          <button
                            onClick={() => handleRestore(menu.id)}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-white text-sm font-medium rounded-lg hover:bg-opacity-90 transition-all duration-200 shadow-sm"
                            style={{ backgroundColor: theme.accent }}
                          >
                            <RestoreIcon className="w-4 h-4" />
                            Restore Menu
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(menu.id, menu.name)}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-all duration-200 shadow-sm bg-red-600"
                            title="Permanently delete this menu - cannot be undone"
                          >
                            <DeleteIcon className="w-4 h-4" />
                            Delete Forever
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Primary Action */}
                          <button
                            onClick={() => handleManageItems(menu.id, menu.name)}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200"
                            style={{
                              // Tokens, not theme.primary — a stale legacy
                              // preset must not repaint the primary action.
                              backgroundColor: 'var(--action)',
                              color: 'var(--action-text)',
                              border: 'none',
                              boxShadow: 'none'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--action-hover)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--action)';
                            }}
                          >
                            <SettingsIcon className="w-4 h-4" />
                            Manage Items
                          </button>

                          {/* Secondary Actions */}
                          <div className="grid grid-cols-3 gap-2">
                            <button
                              onClick={() => handleOpenDialog(menu)}
                              className="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                              title="Edit Menu"
                            >
                              <EditIcon className="w-4 h-4" style={{ color: theme.primary }} />
                              <span className="hidden sm:inline">Edit</span>
                            </button>

                            <button
                              onClick={() => handleOpenAssignDialog(menu)}
                              className="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                              title="Assign Sections"
                            >
                              <LocationIcon className="w-4 h-4" style={{ color: theme.accent }} />
                              <span className="hidden sm:inline">Sections</span>
                            </button>

                            {(() => {
                              const activeMenusCount = menus.filter(m => !m.isDeleted).length;
                              const canDelete = activeMenusCount > 1 && !menu.isDefault;
                              const deleteTooltip = menu.isDefault
                                ? "Cannot delete default menu. Set another menu as default first."
                                : activeMenusCount <= 1
                                  ? "Cannot delete the only menu"
                                  : "Delete Menu";

                              return (
                                <button
                                  onClick={() => canDelete && handleDelete(menu.id)}
                                  disabled={!canDelete}
                                  className={`flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border transition-all duration-200 ${
                                    canDelete
                                      ? 'border-red-200 dark:border-red-600 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer'
                                      : 'border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
                                  }`}
                                  title={deleteTooltip}
                                >
                                  <DeleteIcon className="w-4 h-4" />
                                  <span className="hidden sm:inline">Delete</span>
                                </button>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add/Edit Menu Dialog */}
          <Dialog 
            open={dialogOpen} 
            onClose={handleCloseDialog} 
            maxWidth="md" 
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center"
                     style={{ backgroundColor: theme.button?.secondary?.bg || theme.background }}>
                  <MenuBookIcon className="w-5 h-5" style={{ color: theme.primary }} />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {editingMenu ? 'Edit Menu' : 'Create New Menu'}
                </h2>
              </div>
            </DialogTitle>
            <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <Box sx={{ mt: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  sx={{ 
                    mb: 3,
                    '& .MuiTabs-indicator': { 
                      display: 'none' 
                    },
                    '& .MuiTabs-flexContainer': {
                      gap: { xs: 1, sm: 1.5 },
                      flexWrap: 'nowrap'
                    },
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '10px',
                      minHeight: { xs: 40, sm: 44 },
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.5 },
                      backgroundColor: `${theme.button.secondary.bg} !important`,
                      color: `${theme.button.secondary.text} !important`,
                      border: `1.5px solid ${theme.button.secondary.hover} !important`,
                      transition: 'all 0.2s ease',
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                      '&:hover': {
                        backgroundColor: `${theme.button.secondary.hover} !important`,
                        borderColor: `${theme.button.primary.bg} !important`
                      }
                    },
                    '& .MuiTab-root.Mui-selected': {
                      backgroundColor: `${theme.button.primary.bg} !important`,
                      border: `2px solid ${theme.button.primary.hover} !important`,
                      color: `${theme.button.primary.text} !important`,
                      fontWeight: 700
                    }
                  }}
                >
                  <Tab 
                    label="Basic Info" 
                    value={0}
                  />
                  <Tab 
                    label="Availability" 
                    value={1}
                  />
                </Tabs>

                <Box sx={{ mt: 2 }}>
                  {activeTab === 0 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Menu Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={formData.description || ''}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Menu Type</InputLabel>
                        <Select
                          value={formData.menuType}
                          onChange={(e) => setFormData({ ...formData, menuType: e.target.value })}
                        >
                          {MENU_TYPES.map((type) => (
                            <MuiMenuItem key={type.value} value={type.value}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {type.icon}
                                {type.label}
                              </Box>
                            </MuiMenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isActive}
                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          />
                        }
                        label="Active"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isDefault}
                            onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                          />
                        }
                        label="Default Menu"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: theme.text, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <KitchenIcon />
                          Kitchen Setup
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.gray?.[600] }}>
                          Optional: Create a dedicated kitchen for this menu
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenKitchenDialog(getSuggestedKitchenName(formData.menuType || 'REGULAR'))}
                        fullWidth
                        sx={{
                          backgroundColor: theme.primary + ' !important',
                          color: 'var(--text-inverse) !important',
                          border: 'none !important',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: (theme.primaryHover || theme.primary) + ' !important'
                          }
                        }}
                      >
                        Create New Kitchen
                      </Button>
                    </Grid>
                  </Grid>
                )}

                {activeTab === 1 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TimePicker
                        label="Available From"
                        value={formData.availableFrom ? new Date(`2000-01-01T${formData.availableFrom}`) : null}
                        onChange={(time) => {
                          if (time) {
                            const hours = time.getHours().toString().padStart(2, '0');
                            const minutes = time.getMinutes().toString().padStart(2, '0');
                            setFormData({ ...formData, availableFrom: `${hours}:${minutes}` });
                          }
                        }}
                        slotProps={{ 
                          textField: { 
                            fullWidth: true,
                            placeholder: "hh:mm aa",
                            sx: {
                              '& .MuiInputBase-input': {
                                textAlign: 'center',
                                padding: '8px 10px',
                                height: '36px',
                                fontSize: '14px',
                                '&::placeholder': {
                                  opacity: 0.7,
                                  fontSize: '14px',
                                  letterSpacing: '0.5px'
                                }
                              },
                              '& .MuiInputAdornment-root .MuiIconButton-root': {
                                color: `${theme.primary} !important`,
                                backgroundColor: 'transparent !important',
                                border: 'none !important',
                                boxShadow: 'none !important',
                                padding: '8px',
                                '&:hover': {
                                  backgroundColor: `${theme.button.secondary.bg} !important`,
                                },
                                '& .MuiTouchRipple-root': {
                                  display: 'none'
                                }
                              },
                              '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                                color: `${theme.primary} !important`,
                                fontSize: '1.4rem',
                                opacity: 0.8
                              }
                            }
                          },
                          actionBar: {
                            actions: ['cancel', 'accept'],
                          },
                          dialog: {
                            sx: {
                              '& .MuiDialog-paper': {
                                borderRadius: '16px',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                              },
                              '& .MuiDialogActions-root': {
                                padding: '16px 24px',
                                gap: '12px'
                              },
                              '& .MuiButton-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.9375rem',
                                padding: '8px 20px',
                                borderRadius: '8px',
                                minWidth: '80px',
                                transition: 'all 0.2s ease'
                              },
                              '& .MuiButton-text': {
                                color: `${theme.primary} !important`,
                                backgroundColor: `${theme.button.primary.bg} !important`,
                                '&:hover': {
                                  backgroundColor: `${theme.button.primary.hover} !important`
                                }
                              }
                            }
                          },
                          popper: {
                            sx: { 
                              zIndex: 100002,
                              marginTop: '4px',
                              '& .MuiPickersPopper-root': {
                                zIndex: '100002 !important',
                                transform: 'translateX(-50%)',
                                left: '50% !important'
                              },
                              '& .MuiButton-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.9375rem',
                                padding: '8px 20px',
                                borderRadius: '8px',
                                minWidth: '80px',
                                color: `${theme.primary} !important`,
                                backgroundColor: `${theme.button.primary.bg} !important`,
                                '&:hover': {
                                  backgroundColor: `${theme.button.primary.hover} !important`
                                }
                              }
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TimePicker
                        label="Available To"
                        value={formData.availableTo ? new Date(`2000-01-01T${formData.availableTo}`) : null}
                        onChange={(time) => {
                          if (time) {
                            const hours = time.getHours().toString().padStart(2, '0');
                            const minutes = time.getMinutes().toString().padStart(2, '0');
                            setFormData({ ...formData, availableTo: `${hours}:${minutes}` });
                          }
                        }}
                        slotProps={{ 
                          textField: { 
                            fullWidth: true,
                            placeholder: "hh:mm aa",
                            sx: {
                              '& .MuiInputBase-input': {
                                textAlign: 'center',
                                padding: '8px 10px',
                                height: '36px',
                                fontSize: '14px',
                                '&::placeholder': {
                                  opacity: 0.7,
                                  fontSize: '14px',
                                  letterSpacing: '0.5px'
                                }
                              },
                              '& .MuiInputAdornment-root .MuiIconButton-root': {
                                color: `${theme.primary} !important`,
                                backgroundColor: 'transparent !important',
                                border: 'none !important',
                                boxShadow: 'none !important',
                                padding: '8px',
                                '&:hover': {
                                  backgroundColor: `${theme.button.secondary.bg} !important`,
                                },
                                '& .MuiTouchRipple-root': {
                                  display: 'none'
                                }
                              },
                              '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                                color: `${theme.primary} !important`,
                                fontSize: '1.4rem',
                                opacity: 0.8
                              }
                            }
                          },
                          actionBar: {
                            actions: ['cancel', 'accept'],
                          },
                          dialog: {
                            sx: {
                              '& .MuiDialog-paper': {
                                borderRadius: '16px',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                              },
                              '& .MuiDialogActions-root': {
                                padding: '16px 24px',
                                gap: '12px'
                              },
                              '& .MuiButton-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.9375rem',
                                padding: '8px 20px',
                                borderRadius: '8px',
                                minWidth: '80px',
                                transition: 'all 0.2s ease'
                              },
                              '& .MuiButton-text': {
                                color: `${theme.primary} !important`,
                                backgroundColor: `${theme.button.primary.bg} !important`,
                                '&:hover': {
                                  backgroundColor: `${theme.button.primary.hover} !important`
                                }
                              }
                            }
                          },
                          popper: {
                            sx: { 
                              zIndex: 100002,
                              marginTop: '4px',
                              '& .MuiPickersPopper-root': {
                                zIndex: '100002 !important',
                                transform: 'translateX(-50%)',
                                left: '50% !important'
                              },
                              '& .MuiButton-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.9375rem',
                                padding: '8px 20px',
                                borderRadius: '8px',
                                minWidth: '80px',
                                color: `${theme.primary} !important`,
                                backgroundColor: `${theme.button.primary.bg} !important`,
                                '&:hover': {
                                  backgroundColor: `${theme.button.primary.hover} !important`
                                }
                              }
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Available Days
                      </Typography>
                      <FormGroup row>
                        {WEEKDAYS.map((day) => (
                          <FormControlLabel
                            key={day.value}
                            control={
                              <Checkbox
                                checked={formData.availableDays?.includes(day.value) || false}
                                onChange={(e) => {
                                  const days = formData.availableDays || [];
                                  if (e.target.checked) {
                                    setFormData({ ...formData, availableDays: [...days, day.value] });
                                  } else {
                                    setFormData({ ...formData, availableDays: days.filter(d => d !== day.value) });
                                  }
                                }}
                              />
                            }
                            label={day.label}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                  </Grid>
                )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseDialog}
                sx={{
                  backgroundColor: 'transparent !important',
                  color: `${theme.gray?.[700] || '#374151'} !important`,
                  border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
                  fontWeight: 500,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: `${theme.gray?.[100] || '#f3f4f6'} !important`
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                sx={{
                  backgroundColor: `${theme.primary} !important`,
                  color: 'var(--text-inverse) !important',
                  border: 'none !important',
                  fontWeight: 500,
                  borderRadius: 2,
                  boxShadow: 'none !important',
                  '&:hover': {
                    backgroundColor: `${theme.primaryHover || theme.primary} !important`,
                    boxShadow: 'none !important'
                  }
                }}
              >
                {editingMenu ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Assign Sections Dialog */}
          <Dialog 
            open={assignDialogOpen} 
            onClose={() => setAssignDialogOpen(false)} 
            maxWidth="sm" 
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center"
                     style={{ backgroundColor: theme.accent + '22' }}>
                  <LocationIcon className="w-5 h-5" style={{ color: theme.accent }} />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Assign Sections to {selectedMenu?.name}
                </h2>
              </div>
            </DialogTitle>
            <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedMenu?.isDefault
                  ? 'Default menu automatically covers unassigned sections. Sections already assigned to other menus are shown disabled.'
                  : 'Select the sections where this menu should be available'}
              </Typography>
              <List>
                {/* Takeaway Orders - Special entry at top */}
                <ListItem
                  sx={{
                    backgroundColor: theme.button.secondary.bg,
                    borderRadius: 1,
                    mb: 1,
                    border: `1px solid ${theme.button.secondary.hover}`
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 2 }}>
                    <TakeawayIcon sx={{ color: theme.primary }} />
                  </Box>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 600, color: theme.text }}>
                        Takeaway Orders
                      </Typography>
                    }
                    secondary="Use this menu for all takeaway/pickup orders"
                  />
                  <ListItemSecondaryAction>
                    <Checkbox
                      edge="end"
                      disabled={selectedMenu?.isDefault}
                      checked={selectedSections.includes(TAKEAWAY_SECTION_ID)}
                      onChange={(e) => {
                        if (selectedMenu?.isDefault) return;
                        if (e.target.checked) {
                          setSelectedSections([...selectedSections, TAKEAWAY_SECTION_ID]);
                        } else {
                          setSelectedSections(selectedSections.filter(id => id !== TAKEAWAY_SECTION_ID));
                        }
                      }}
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" sx={{ color: theme.gray?.[500] }}>
                    Dine-in Sections
                  </Typography>
                </Divider>

                {dedupedSections.map((section) => {
                  const assignedMenu = sectionIdToAssignedMenu.get(section.id);
                  const isDisabled = Boolean(selectedMenu?.isDefault && assignedMenu);
                  return (
                    <ListItem key={section.id} sx={isDisabled ? { opacity: 0.5 } : undefined}>
                      <ListItemText
                        primary={section.name}
                        secondary={selectedMenu?.isDefault && assignedMenu ? `Assigned to ${assignedMenu.name}` : section.floorName}
                      />
                      <ListItemSecondaryAction>
                        <Checkbox
                          edge="end"
                          disabled={selectedMenu?.isDefault}
                          checked={selectedMenu?.isDefault ? !assignedMenu : selectedSections.includes(section.id)}
                          onChange={(e) => {
                            if (selectedMenu?.isDefault) return; // read-only for default
                            if (e.target.checked) {
                              setSelectedSections([...selectedSections, section.id]);
                            } else {
                              setSelectedSections(selectedSections.filter(id => id !== section.id));
                            }
                          }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setAssignDialogOpen(false)}
                sx={{
                  backgroundColor: 'transparent !important',
                  color: `${theme.gray?.[700] || '#374151'} !important`,
                  border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
                  fontWeight: 500,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: `${theme.gray?.[100] || '#f3f4f6'} !important`
                  }
                }}
              >
                Cancel
              </Button>
              {!selectedMenu?.isDefault && (
                <Button
                  onClick={handleAssignSections}
                  variant="contained"
                  sx={{
                    backgroundColor: `${theme.primary} !important`,
                    color: 'var(--text-inverse) !important',
                    border: 'none !important',
                    fontWeight: 500,
                    borderRadius: 2,
                    boxShadow: 'none !important',
                    '&:hover': {
                      backgroundColor: `${theme.primaryHover || theme.primary} !important`,
                      boxShadow: 'none !important'
                    }
                  }}
                >
                  Assign Sections
                </Button>
              )}
            </DialogActions>
          </Dialog>

          {/* Kitchen Creation Dialog */}
          <Dialog 
            open={kitchenDialogOpen} 
            onClose={handleCloseKitchenDialog} 
            maxWidth="sm" 
            fullWidth
            fullScreen={isMobile}
          >
            <DialogTitle>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center"
                     style={{ backgroundColor: theme.accent + '22' }}>
                  <KitchenIcon className="w-5 h-5" style={{ color: theme.accent }} />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Create New Kitchen
                </h2>
              </div>
            </DialogTitle>
            <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Kitchen Name"
                      value={kitchenFormData.name}
                      onChange={(e) => setKitchenFormData({ ...kitchenFormData, name: e.target.value })}
                      required
                      placeholder="e.g., Bar Kitchen, Main Kitchen"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Kitchen Type/Area (Optional)"
                      value={kitchenFormData.type}
                      onChange={(e) => setKitchenFormData({ ...kitchenFormData, type: e.target.value })}
                      placeholder="e.g., Bar Area, Pool Area, Main Dining, VIP Section"
                      helperText="Optional: Specify the type or area this kitchen serves"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={kitchenFormData.description}
                      onChange={(e) => setKitchenFormData({ ...kitchenFormData, description: e.target.value })}
                      multiline
                      rows={2}
                      placeholder="Brief description of this kitchen's purpose"
                    />
                  </Grid>
                  {printers.length > 0 && (
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Printer (Optional)</InputLabel>
                        <Select
                          value={kitchenFormData.printerId}
                          onChange={(e) => setKitchenFormData({ ...kitchenFormData, printerId: e.target.value })}
                          label="Printer (Optional)"
                        >
                          <MuiMenuItem value="">
                            <em>No Printer</em>
                          </MuiMenuItem>
                          {printers.map((printer) => (
                            <MuiMenuItem key={printer.id} value={printer.id.toString()}>
                              {printer.name} ({printer.ipAddress})
                            </MuiMenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={kitchenFormData.isActive}
                          onChange={(e) => setKitchenFormData({ ...kitchenFormData, isActive: e.target.checked })}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px', gap: '12px' }}>
              <Button
                onClick={handleCloseKitchenDialog}
                disableElevation
                sx={{
                  backgroundColor: 'transparent !important',
                  color: `${theme.gray?.[700] || '#374151'} !important`,
                  border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                  padding: '10px 24px',
                  borderRadius: '10px',
                  textTransform: 'none',
                  transition: 'all 0.2s ease',
                  minWidth: '100px',
                  boxShadow: 'none !important',
                  '&:hover': {
                    backgroundColor: `${theme.gray?.[100] || '#f3f4f6'} !important`,
                    boxShadow: 'none !important'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateKitchen}
                disabled={!kitchenFormData.name.trim()}
                disableElevation
                sx={{
                  backgroundColor: `${theme.primary} !important`,
                  color: 'var(--text-inverse) !important',
                  border: 'none !important',
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                  padding: '10px 24px',
                  borderRadius: '10px',
                  textTransform: 'none',
                  transition: 'all 0.2s ease',
                  minWidth: '140px',
                  boxShadow: 'none !important',
                  '&:hover': {
                    backgroundColor: `${theme.primaryHover || theme.primary} !important`,
                    boxShadow: 'none !important'
                  },
                  '&:disabled': {
                    backgroundColor: `${theme.gray?.[300]} !important`,
                    color: `${theme.gray?.[500]} !important`,
                    borderColor: `${theme.gray?.[300]} !important`,
                    boxShadow: 'none !important',
                    opacity: 0.6
                  }
                }}
              >
                Create Kitchen
              </Button>
            </DialogActions>
          </Dialog>

          {/* Select New Default Menu Dialog */}
          <Dialog
            open={selectNewDefaultDialogOpen}
            onClose={() => {
              setSelectNewDefaultDialogOpen(false);
              setPendingSaveData(null);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ backgroundColor: theme.background, color: theme.text, borderBottom: `1px solid ${theme.gray?.[200]}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon sx={{ color: theme.warning }} />
                Select New Default Menu
              </Box>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: theme.background, color: theme.text, pt: 3, maxHeight: '80vh', overflowY: 'auto' }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                You must select another menu to become the default menu before removing default status from <strong>{editingMenu?.name}</strong>.
              </Alert>
              <Typography variant="body2" sx={{ mb: 2, color: theme.gray?.[600] }}>
                Select which menu should be the new default:
              </Typography>
              <List>
                {menus
                  .filter(m =>
                    m.id !== editingMenu?.id &&
                    m.isActive &&
                    !m.isDeleted
                  )
                  .map((menu) => (
                    <ListItem
                      key={menu.id}
                      button
                      onClick={() => handleSelectNewDefault(menu.id)}
                      sx={{
                        border: `1px solid ${theme.gray?.[200]}`,
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: theme.accent + '10',
                          borderColor: theme.accent
                        }
                      }}
                    >
                      <ListItemText
                        primary={menu.name}
                        secondary={`${menu.menuType} • ${menu.itemCount || 0} items`}
                        primaryTypographyProps={{
                          sx: { color: theme.text, fontWeight: 500 }
                        }}
                        secondaryTypographyProps={{
                          sx: { color: theme.gray?.[500] }
                        }}
                      />
                    </ListItem>
                  ))}
              </List>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: theme.background, borderTop: `1px solid ${theme.gray?.[200]}` }}>
              <Button
                onClick={() => {
                  setSelectNewDefaultDialogOpen(false);
                  setPendingSaveData(null);
                }}
                sx={{
                  color: theme.text,
                  '&:hover': {
                    backgroundColor: theme.background + '10'
                  }
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          {/* Success/Error Notifications */}
          <Snackbar 
            open={!!success} 
            autoHideDuration={4000} 
            onClose={() => setSuccess(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setSuccess(null)} 
              severity="success" 
              sx={{ width: '100%' }}
            >
              {success}
            </Alert>
          </Snackbar>

          <Snackbar 
            open={!!error} 
            autoHideDuration={6000} 
            onClose={() => setError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setError(null)} 
              severity="error" 
              sx={{ width: '100%' }}
            >
              {error}
            </Alert>
          </Snackbar>
        </Container>
    </LocalizationProvider>
  );
};

export default MultiMenuManagement;